/**
 * Main Application Component
 * 
 * Defines all routes for the multi-tenant revenue collection platform.
 * Routes are organized into:
 * - Authentication routes (login, register)
 * - Tenant selection route
 * - Tenant admin routes (dashboard, revenue, demand notices, etc.)
 * - Super Admin routes (platform management)
 * 
 * @module App
 */

import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom'
import { lazy, Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { buildTenantAccessFallback, canAccessBillableModule, canAccessOwnerReportsWorkspace, canAccessTrackingModule, canUseExternalBillImport, canGenerateDemandNotices, canManageDemandNoticeCategories, canManageDistributionClaims, canManageLocations, canManagePlatformTenants, canManagePlatformUsers, canManageRevenueHeads, canManageTenantUsers, canReviewDistributions, canViewAuditLogs, canViewBulkBillImportHistory, canViewDistributionReports, canViewFinanceReports, canViewOwnerReports, canViewPlatformAnalytics, canViewPlatformAuditLogs, getPlatformHomeRoute, getScopedTenantAccess, getTenantHomeRoute, isPlatformUser } from './utils/accessControl';
import DashboardRuntimeGuard from './components/DashboardRuntimeGuard';
import RouteErrorBoundary from './components/RouteErrorBoundary';

import { USER_ROLES } from './constants/roles'

// Route screens are lazy-loaded to keep the initial dashboard bundle smaller.
const DashboardScreen = lazy(() => import('./screens/DashboardScreen.js'));
const DemandModuleScreen = lazy(() => import('./screens/DemandModuleScreen.js'));
const DemandSelectionScreen = lazy(() => import('./screens/DemandSelectionScreen.js'));
const RevenueListScreen = lazy(() => import('./screens/RevenueListScreen.js'));
const RevenueEditScreen = lazy(() => import('./screens/RevenueEditScreen.js'));
const CategoryEditScreen = lazy(() => import('./screens/CategoryEditScreen.js'));
const CategoryListScreen = lazy(() => import('./screens/CategoryListScreen.js'));
const BatchPreviewScreen = lazy(() => import('./screens/BatchPreviewScreen.js'));
const SpecificPreviewScreen = lazy(() => import('./screens/SpecificPreviewScreen.js'));
const DemandNoticeReceiptScreen = lazy(() => import('./screens/DemandNoticeReceiptScreen.js'));
const GeneratedListScreen = lazy(() => import('./screens/GeneratedListScreen.js'));
const CreateCategoryScreen = lazy(() => import('./screens/CreateCategoryScreen.js'));
const TenantUserScreen = lazy(() => import('./screens/TenantUserScreen.js'));
const CategoryDetailRevenueScreen = lazy(() => import('./screens/CategoryDetailRevenueScreen.js'));
const TenantAuditLogsScreen = lazy(() => import('./screens/TenantAuditLogsScreen.js'));
const LoginScreen = lazy(() => import('./screens/LoginScreen.js'));
const ForgotPasswordScreen = lazy(() => import('./screens/ForgotPasswordScreen.js'));
const ResetPasswordScreen = lazy(() => import('./screens/ResetPasswordScreen.js'));
const NotFoundScreen = lazy(() => import('./screens/NotFoundScreen.js'));
const SuperAdminAccountScreen = lazy(() => import('./screens/SuperAdminAccountScreen.js'));
const TenancyProfileScreen = lazy(() => import('./screens/TenancyProfileScreen.js'));
const AccountSettingsScreen = lazy(() => import('./screens/AccountSettingsScreen.js'));
const TaxOfficeManagementScreen = lazy(() => import('./screens/ZoneManagementScreen.js'));
const DistributionZoneManagementScreen = lazy(() => import('./screens/DistributionZoneManagementScreen.js'));
const FieldAgentAccessNoticeScreen = lazy(() => import('./screens/FieldAgentAccessNoticeScreen.js'));
const SuperAdminDashboardScreen = lazy(() => import('./screens/SuperAdminDashboardScreen.js'));
const SuperAdminTenantsScreen = lazy(() => import('./screens/SuperAdminTenantsScreen.js'));
const SuperAdminCreateTenantScreen = lazy(() => import('./screens/SuperAdminCreateTenantScreen.js'));
const SuperAdminTenantDetailsScreen = lazy(() => import('./screens/SuperAdminTenantDetailsScreen.js'));
const SuperAdminAuditLogsScreen = lazy(() => import('./screens/SuperAdminAuditLogsScreen.js'));
const SuperAdminAnalyticsScreen = lazy(() => import('./screens/SuperAdminAnalyticsScreen.js'));
const SuperAdminOwnerReportsScreen = lazy(() => import('./screens/SuperAdminOwnerReportsScreen.js'));
const SuperAdminOwnerReportWorkspaceScreen = lazy(() => import('./screens/SuperAdminOwnerReportWorkspaceScreen.js'));
const SuperAdminPlatformUsersScreen = lazy(() => import('./screens/SuperAdminPlatformUsersScreen.js'));
const SuperAdminAccessScreen = lazy(() => import('./screens/SuperAdminAccessScreen.js'));
const DistributionDashboardScreen = lazy(() => import('./screens/DistributionDashboardScreen.js'));
const DistributionListScreen = lazy(() => import('./screens/DistributionListScreen.js'));
const DistributionReviewScreen = lazy(() => import('./screens/DistributionReviewScreen.js'));
const DistributionClaimsScreen = lazy(() => import('./screens/DistributionClaimsScreen.js'));
const DistributionMapScreen = lazy(() => import('./screens/DistributionMapScreen.js'));
const DistributionDetailScreen = lazy(() => import('./screens/DistributionDetailScreen.js'));
const DistributionReportRecordsScreen = lazy(() => import('./screens/DistributionReportRecordsScreen.js'));
const FinalReminderNoticeScreen = lazy(() => import('./screens/FinalReminderNoticeScreen.js'));
const PaymentInformationScreen = lazy(() => import('./screens/PaymentInformationScreen.js'));
const ExternalBillImportScreen = lazy(() => import('./screens/ExternalBillImportScreen.js'));
const ExternalBillBatchDetailScreen = lazy(() => import('./screens/ExternalBillBatchDetailScreen.js'));

const routeFallback = (
   <div className="min-h-screen flex items-center justify-center bg-gray-50 text-sm text-gray-500">
      Loading...
   </div>
);

function App() {
   const { userInfo } = useSelector((state) => state.userLogin || {});
   const { tenant } = useSelector((state) => state.tenantDashboardDetails || {});
   const { access: tenantAccess, loading: tenantAccessLoading, error: tenantAccessError } = useSelector((state) => state.userTenantAccess || {});
   const tenantId = tenant?.singleTenant?._id || userInfo?.tenantId || '';
   const scopedTenantAccess = getScopedTenantAccess(tenantAccess, tenantId);
   const solutionModeAccess = scopedTenantAccess || buildTenantAccessFallback(userInfo, tenant?.singleTenant);
   const resolvedSolutionMode = scopedTenantAccess?.solutionMode || tenant?.singleTenant?.solutionMode || userInfo?.solutionMode || '';
   const tenantHomeRoute = getTenantHomeRoute(solutionModeAccess);
   const accountSettingsElement = useMemo(() => <AccountSettingsScreen />, []);

    const renderTenantModeRoute = (element, options = {}) => {
      const { requiresBillable = false, requiresTracking = false, requiresTrackingOnly = false, permissionCheck, requiresResolvedTenantAccess = false } = options;

      if (!userInfo) {
         return <Navigate to="/" replace />;
      }

      if (isPlatformUser(userInfo)) {
         return <Navigate to={getPlatformHomeRoute(userInfo)} replace />;
      }

      if (userInfo?.role === USER_ROLES.FIELD_AGENT) {
         return <Navigate to="/field-agent-access" replace />;
      }

      if (requiresResolvedTenantAccess) {
         if (!tenantId) {
            return <Navigate to="/tenancy_profile" replace />;
         }

         if (tenantAccessLoading || (!scopedTenantAccess && !tenantAccessError)) {
            return (
               <div className="min-h-screen flex items-center justify-center bg-gray-50 text-sm text-gray-500">
                  Loading tenant access...
               </div>
            );
         }

         if (!scopedTenantAccess) {
            return <Navigate to="/tenancy_profile" replace />;
         }
      }

      if ((requiresBillable || requiresTracking || requiresTrackingOnly) && tenantId && !resolvedSolutionMode) {
         return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 text-sm text-gray-500">
               Loading tenant access...
            </div>
         );
      }

       if (requiresBillable && !canAccessBillableModule(solutionModeAccess)) {
          return <Navigate to={tenantHomeRoute} replace />;
       }

       if (requiresTracking && !canAccessTrackingModule(solutionModeAccess)) {
          return <Navigate to={tenantHomeRoute} replace />;
       }

       if (requiresTrackingOnly && !canUseExternalBillImport(solutionModeAccess)) {
          return <Navigate to={tenantHomeRoute} replace />;
       }

       if (typeof permissionCheck === 'function' && !permissionCheck(solutionModeAccess)) {
          return <Navigate to={tenantHomeRoute} replace />;
       }

       return element;
    };

    const renderPlatformRoute = (element, permissionCheck) => {
       if (!userInfo) {
          return <Navigate to="/" replace />;
       }

       if (!isPlatformUser(userInfo)) {
          return <Navigate to="/tenancy_profile" replace />;
       }

       if (typeof permissionCheck === 'function' && !permissionCheck(userInfo)) {
          return <Navigate to={getPlatformHomeRoute(userInfo)} replace />;
       }

       return element;
    };

   return (
      <>

          <BrowserRouter>
             <DashboardRuntimeGuard />
             <RouteErrorBoundary>
             <Suspense fallback={routeFallback}>
             <Routes>
               <Route path="/" element={<LoginScreen />} />
               {/* Emails and old bookmarks use /login; the dashboard signs in at its root. */}
               <Route exact path="/login" element={<Navigate to="/" replace />} />
               <Route exact path="/forgot-password" element={<ForgotPasswordScreen />} />
               <Route exact path="/reset-password" element={<ResetPasswordScreen />} />
                <Route exact path="/tenancy_profile" element={renderTenantModeRoute(<TenancyProfileScreen />)} />
                <Route exact path="/field-agent-access" element={<FieldAgentAccessNoticeScreen />} />
                  <Route exact path="/account/settings" element={renderTenantModeRoute(accountSettingsElement)} />
                  <Route exact path="/account/tax-offices" element={renderTenantModeRoute(<TaxOfficeManagementScreen />, { requiresBillable: true, permissionCheck: canManageLocations })} />
                  <Route exact path="/account/distribution-zones" element={renderTenantModeRoute(<DistributionZoneManagementScreen />, { requiresTrackingOnly: true, permissionCheck: canManageLocations })} />
                  <Route exact path="/account/zones" element={<Navigate to="/account/tax-offices" replace />} />
               {/* <Route exact path="/:id/:name/dashboard/profile" element={<DashboardScreen />} /> */}
                <Route
                   exact
                   path="/dashboard/profile"
                    element={renderTenantModeRoute(<DashboardScreen />)}
                 />
                 <Route
                    exact
                    path="/dashboard/payments"
                    element={renderTenantModeRoute(<PaymentInformationScreen />, { requiresBillable: true, permissionCheck: canViewFinanceReports })}
                 />
                 <Route
                    exact
                    path="/demand_module"
                   element={renderTenantModeRoute(<DemandModuleScreen />, { requiresBillable: true })}
                />
                 <Route
                    exact
                    path="/demand_selection"
                    element={renderTenantModeRoute(<DemandSelectionScreen />, { requiresBillable: true, permissionCheck: canGenerateDemandNotices, requiresResolvedTenantAccess: true })}
                 />
               <Route exact path="/DemandNotice" element={<Navigate to="/dashboard/profile" replace />} />
               <Route exact path="/blank" element={<Navigate to="/dashboard/profile" replace />} />
                 <Route
                    exact
                    path="/category"
                    element={renderTenantModeRoute(<CategoryListScreen />, { requiresBillable: true, permissionCheck: canManageDemandNoticeCategories })}
                 />
                 <Route
                    exact
                    path="/category/create"
                    element={renderTenantModeRoute(<CreateCategoryScreen />, { requiresBillable: true, permissionCheck: canManageDemandNoticeCategories })}
                 />
                 <Route
                    exact
                    path="/revenuelines/:id/edit"
                    element={renderTenantModeRoute(<RevenueEditScreen />, { requiresBillable: true, permissionCheck: canManageRevenueHeads })}
                 />
                 <Route
                    exact
                    path="/demand-notice-categories/:id/edit"
                    element={renderTenantModeRoute(<CategoryEditScreen />, { requiresBillable: true, permissionCheck: canManageDemandNoticeCategories })}
                 />

                <Route
                   exact
                   path="/users/admin"
                   element={renderTenantModeRoute(<TenantUserScreen />, { permissionCheck: canManageTenantUsers })}
                />
                <Route
                   exact
                   path="/audit-logs"
                   element={renderTenantModeRoute(<TenantAuditLogsScreen />, { permissionCheck: canViewAuditLogs })}
                />
                 <Route
                    exact
                    path="/revenue"
                    element={renderTenantModeRoute(<RevenueListScreen />, { requiresBillable: true, permissionCheck: canManageRevenueHeads })}
                 />
                <Route
                   exact
                   path="/demand-notices/:id"
                   //  path="/withdrawals/:id/edit"
                   element={renderTenantModeRoute(<BatchPreviewScreen />, { requiresBillable: true })}
                />
                <Route
                   exact
                   path="/demand-notices/specific/:id"

                   element={renderTenantModeRoute(<SpecificPreviewScreen />, { requiresBillable: true })}
                />
                <Route
                   exact
                   path="/demand-notices/specific/:id/receipt"
                   element={renderTenantModeRoute(<DemandNoticeReceiptScreen />, { requiresBillable: true })}
                />
                 <Route
                    exact
                    path="demand-notice-categories/:id"
                    element={renderTenantModeRoute(<CategoryDetailRevenueScreen />, { requiresBillable: true, permissionCheck: canManageDemandNoticeCategories })}
                 />
                 <Route
                    exact
                    path="/demand-notices/batches"
                    element={renderTenantModeRoute(<GeneratedListScreen />, { requiresBillable: true })}
                 />

                {/* ========================================== */}
                {/* Distribution Routes - Field Agent Tracking */}
                {/* ========================================== */}
                  <Route
                     exact
                     path="/distributions/dashboard"
                     element={renderTenantModeRoute(<DistributionDashboardScreen />, { requiresTracking: true, permissionCheck: canViewDistributionReports })}
                  />
                  <Route
                     exact
                     path="/distributions/list"
                     element={renderTenantModeRoute(<DistributionListScreen />, { requiresTracking: true, permissionCheck: canViewDistributionReports })}
                  />
                  <Route
                     exact
                     path="/distributions/flagged"
                     element={renderTenantModeRoute(<DistributionReviewScreen />, { requiresTracking: true, permissionCheck: canReviewDistributions })}
                  />
                  <Route
                     exact
                     path="/distributions/claims"
                     element={renderTenantModeRoute(<DistributionClaimsScreen />, { requiresTracking: true, permissionCheck: canManageDistributionClaims })}
                  />
                  <Route
                     exact
                     path="/distributions/map"
                     element={renderTenantModeRoute(<DistributionMapScreen />, { requiresTracking: true, permissionCheck: canViewDistributionReports })}
                  />
                  <Route
                     exact
                     path="/distributions/reports/records"
                     element={renderTenantModeRoute(<DistributionReportRecordsScreen />, { requiresTracking: true, permissionCheck: canViewDistributionReports })}
                  />
                 <Route
                    exact
                    path="/distributions/reminders"
                    element={renderTenantModeRoute(<FinalReminderNoticeScreen />, { requiresBillable: true, requiresTracking: true })}
                 />
                   <Route
                      exact
                      path="/distributions/import"
                      element={renderTenantModeRoute(<ExternalBillImportScreen />, { requiresTracking: true, requiresTrackingOnly: true, permissionCheck: canViewDistributionReports })}
                   />
                   <Route
                      exact
                      path="/distributions/import/batches/:importBatchId"
                      element={renderTenantModeRoute(<ExternalBillBatchDetailScreen />, { requiresTracking: true, permissionCheck: canViewDistributionReports })}
                   />
                   <Route
                      exact
                      path="/distributions/bill-upload"
                      element={renderTenantModeRoute(<ExternalBillImportScreen />, { requiresTracking: true, permissionCheck: canViewBulkBillImportHistory })}
                   />
                   <Route
                      exact
                      path="/distributions/bill-upload/batches/:importBatchId"
                      element={renderTenantModeRoute(<ExternalBillBatchDetailScreen />, { requiresTracking: true, permissionCheck: canViewBulkBillImportHistory })}
                   />
                   <Route
                      exact
                      path="/distributions/enugu-import"
                      element={renderTenantModeRoute(<ExternalBillImportScreen />, { requiresTracking: true, permissionCheck: canViewBulkBillImportHistory })}
                   />
                  <Route
                     exact
                     path="/distributions/:id"
                     element={renderTenantModeRoute(<DistributionDetailScreen />, { requiresTracking: true, permissionCheck: canViewDistributionReports })}
                  />

                {/* ========================================== */}
                {/* Super Admin Routes - Platform Management  */}
                {/* ========================================== */}
               <Route
                  exact
                  path="/super-admin/dashboard"
                  element={renderPlatformRoute(<SuperAdminDashboardScreen />, canViewPlatformAnalytics)}
               />
               <Route
                  exact
                  path="/super-admin/tenants"
                  element={renderPlatformRoute(<SuperAdminTenantsScreen />, canManagePlatformTenants)}
               />
               <Route
                  exact
                  path="/super-admin/tenants/new"
                  element={renderPlatformRoute(<SuperAdminCreateTenantScreen />, canManagePlatformTenants)}
               />
               <Route
                  exact
                  path="/super-admin/tenants/:id"
                  element={renderPlatformRoute(<SuperAdminTenantDetailsScreen />, canManagePlatformTenants)}
               />
               <Route
                  exact
                  path="/super-admin/audit-logs"
                  element={renderPlatformRoute(<SuperAdminAuditLogsScreen />, canViewPlatformAuditLogs)}
               />
                <Route
                   exact
                   path="/super-admin/analytics"
                   element={renderPlatformRoute(<SuperAdminAnalyticsScreen />, canViewPlatformAnalytics)}
                />
                 <Route
                    exact
                    path="/super-admin/owner-reports"
                    element={renderPlatformRoute(<SuperAdminOwnerReportsScreen />, canAccessOwnerReportsWorkspace)}
                 />
                 <Route
                    exact
                    path="/super-admin/owner-reports/:configId/run"
                    element={renderPlatformRoute(<SuperAdminOwnerReportWorkspaceScreen />, canViewOwnerReports)}
                 />
                 <Route
                    exact
                    path="/super-admin/platform-users"
                    element={renderPlatformRoute(<SuperAdminPlatformUsersScreen />, canManagePlatformUsers)}
                 />
                 <Route
                    exact
                    path="/super-admin/access"
                    element={renderPlatformRoute(<SuperAdminAccessScreen />)}
                 />
                 <Route
                    exact
                    path="/super-admin/account"
                    element={renderPlatformRoute(<SuperAdminAccountScreen />)}
                 />
                 <Route path="*" element={<NotFoundScreen />} />
              </Routes>
             </Suspense>
             </RouteErrorBoundary>
          </BrowserRouter>
         {/* <Footer /> */}
      </>
   )
}

export default App
